import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';

import type {
  ImportViolation,
  MovedTask,
  RunningTask,
  WorkflowView,
} from '@/lib/api/generated/model';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAssignablePeople, useIsOrgAdmin } from '@/features/identity';
import { useLlmProfiles } from '@/features/llm-profiles';
import { isWorkflowRejection, isWorkflowUpdateBlocked } from '../errors';
import { useImportUpdateWorkflow } from '../hooks/useImportUpdateWorkflow';
import { useImportWorkflow } from '../hooks/useImportWorkflow';
import { usePreflightImportUpdate } from '../hooks/usePreflightImportUpdate';
import { usePreflightWorkflow } from '../hooks/usePreflightWorkflow';
import { keyClash } from '../upload/keyClash';
import {
  checkWorkflowFile,
  isBundle,
  type PreCheckFailure,
} from '../upload/preChecks';
import { counted } from '../upload/bundleReview';
import {
  initialAnswers,
  outstanding,
  toRequest,
  toRows,
  type Answers,
} from '../upload/answers';
import { BundleImport } from './BundleImport';
import { FileDrop } from './FileDrop';
import { FlowDialog } from './FlowDialog';
import { ResolveReferences } from './ResolveReferences';

/**
 * One violation, rendered the way the contract shipped it: the message
 * verbatim, the path when the violation is locatable, line and column for
 * parse failures. Unsupported-shape violations look different from malformed-
 * file ones — they announce a temporary limit, not a broken file (FR-026).
 */
function ViolationItem({ violation }: { violation: ImportViolation }) {
  const isShape = violation.category === 'unsupported_shape';
  return (
    <li
      className={cn(
        'flex flex-col gap-0.5 rounded-md border p-2',
        isShape
          ? 'border-foreground/30 bg-muted/60 border-dashed'
          : 'border-border bg-muted/30',
      )}
    >
      {isShape && (
        <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          Not supported yet
        </span>
      )}
      <span className="text-foreground text-sm">{violation.message}</span>
      {violation.path != null && (
        <code className="text-muted-foreground text-xs">{violation.path}</code>
      )}
      {violation.line != null && (
        <span className="text-muted-foreground text-xs">
          line {violation.line}
          {violation.column != null ? `, column ${violation.column}` : ''}
        </span>
      )}
    </li>
  );
}

/** One task blocking an update: who it is, where, and what it is doing. */
export function RunningTaskItem({ task }: { task: RunningTask }) {
  return (
    <li className="border-border bg-muted/30 flex flex-col gap-0.5 rounded-md border p-2">
      <span className="text-foreground text-sm">{task.task_title}</span>
      <span className="text-muted-foreground text-xs">
        {task.step_id} · {task.execution_status}
      </span>
    </li>
  );
}

/** One task the update would move to Backlog: informational, not a problem. */
export function MovedTaskItem({ task }: { task: MovedTask }) {
  return (
    <li className="border-border bg-muted/30 flex flex-col gap-0.5 rounded-md border p-2">
      <span className="text-foreground text-sm">{task.task_title}</span>
      <span className="text-muted-foreground text-xs">
        {task.from_step} → Backlog
      </span>
    </li>
  );
}

/**
 * The Upload-from-YAML dialog.
 *
 * Two faces, not a wizard (spec 102 research R3). The first is the file picker
 * it has always been. Pressing Upload asks the service what the file would need
 * here; when the answer is "nothing", the import goes ahead in the same press,
 * so a file that resolves costs exactly what it always did. When the answer
 * names something that cannot be found, the dialog shows the second face: one
 * row per unresolved reference, and nothing is created until the person
 * confirms.
 *
 * The examination happens on the press rather than on choosing the file
 * (research R4). Choosing spends nothing, so picking the wrong file twice costs
 * nothing, and the wait sits where the request does.
 *
 * A third face, spec 245: a chosen file whose `key` matches a workflow already
 * in the workspace offers "Update this workflow" instead of the create door's
 * dead end. That match is checked client-side before any call to either door
 * (FR-001) — the create door's own `WORKFLOW_KEY_CONFLICT` only names the
 * clashing workflow, not its id, so it cannot drive this choice, and stays as
 * a backstop for a key that clashes concurrently (FR-008).
 *
 * On rejection the dialog stays open with the file still selected, so the
 * author can fix the file and retry from where they stood (FR-021); every
 * violation renders, each with its location (FR-014). While a request is in
 * flight nothing can be re-submitted (FR-011). Success closes the dialog —
 * the list updating without a reload is the mutation's cache invalidation,
 * not this component's concern.
 */
export function UploadWorkflowDialog({
  workspaceId,
  workflows,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  /** The workspace's workflows, for the client-side key-clash check. */
  workflows: WorkflowView[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const fileSelectionToken = useRef(0);
  const [preCheckFailure, setPreCheckFailure] =
    useState<PreCheckFailure | null>(null);
  const [answers, setAnswers] = useState<Answers | null>(null);
  const preCheckErrorId = useId();

  // The clashing workflow, once the file has been checked against the
  // workspace's list. `undefined` means "not checked yet"; `null` means
  // "checked, no clash" (today's flow).
  const [updateTarget, setUpdateTarget] = useState<
    WorkflowView | null | undefined
  >(undefined);
  const updateMode = updateTarget != null;

  const importMutation = useImportWorkflow(workspaceId);
  const preflightMutation = usePreflightWorkflow(workspaceId);
  const importUpdateMutation = useImportUpdateWorkflow(workspaceId);
  const preflightImportUpdateMutation = usePreflightImportUpdate(workspaceId);
  const [bundleBusy, setBundleBusy] = useState(false);
  // What the bundle's check said it holds, for the file chip (spec 208).
  const [described, setDescribed] = useState<{
    root: string;
    items: number;
  } | null>(null);
  const isPending =
    importMutation.isPending ||
    preflightMutation.isPending ||
    importUpdateMutation.isPending ||
    preflightImportUpdateMutation.isPending ||
    bundleBusy;
  // A .zip is a bundle (spec 204): a whole set of workflows, asked about and
  // imported together by its own flow below. A .yaml keeps this dialog's own.
  const bundle =
    file !== null && preCheckFailure === null && isBundle(file) ? file : null;

  // Both pickers read what already exists elsewhere in the product; neither is
  // new data for this dialog (research R5, R6).
  const profiles = useLlmProfiles();
  const people = useAssignablePeople(workspaceId);
  const isOrgAdmin = useIsOrgAdmin();

  const report = preflightMutation.data;
  const rows = report ? toRows(report.unresolved ?? []) : [];
  const resolving = answers !== null && rows.length > 0;

  const updatePreflight = preflightImportUpdateMutation.data;
  const running = updatePreflight?.running ?? [];
  const wouldMove = updatePreflight?.would_move ?? [];
  const updateViolations = updatePreflight?.violations ?? [];

  const reset = () => {
    setFile(null);
    setPreCheckFailure(null);
    setAnswers(null);
    setUpdateTarget(undefined);
    importMutation.reset();
    preflightMutation.reset();
    importUpdateMutation.reset();
    preflightImportUpdateMutation.reset();
  };

  const handleOpenChange = (next: boolean) => {
    if (isPending) return;
    onOpenChange(next);
    // Closing discards the answers with everything else: nothing here is
    // remembered between uploads (FR-013).
    if (!next) reset();
  };

  const send = (chosen: File, given: Answers | null) => {
    importMutation.mutate(
      { file: chosen, answers: given ? toRequest(given) : undefined },
      {
        onSuccess: (result) => {
          onOpenChange(false);
          reset();
          announce(result.warnings ?? [], result.substitutions ?? []);
        },
      },
    );
  };

  const sendUpdate = (chosen: File, target: WorkflowView) => {
    importUpdateMutation.mutate(
      { workflowId: target.id, file: chosen },
      {
        onSuccess: (result) => {
          onOpenChange(false);
          reset();
          announceUpdate(result.moved_tasks ?? []);
        },
      },
    );
  };

  const updatePreflightFailed =
    updateMode && preflightImportUpdateMutation.isError;

  const updateBlocked =
    updateMode &&
    (updatePreflightFailed ||
      running.length > 0 ||
      updateViolations.length > 0 ||
      updatePreflight?.importable === false);

  const submit = () => {
    // A file the browser can already tell is wrong is refused here, so no
    // request is sent at all (FR-009/FR-010).
    if (!file || isPending || preCheckFailure !== null) return;

    if (updateMode) {
      if (updateBlocked) return;
      sendUpdate(file, updateTarget);
      return;
    }

    if (resolving) {
      // Second press, from the resolution face: the answers travel with it.
      send(file, answers);
      return;
    }

    preflightMutation.mutate(file, {
      onSuccess: (checked) => {
        const found = toRows(checked.unresolved ?? []);
        if (found.length > 0) {
          /*
            Anything unresolved opens the second face, blocking or not. A person
            nobody matches would import perfectly well without being mentioned —
            that is exactly what happens today — but saying nothing would mean
            they find out a gate routes to nobody when it stalls. It is listed,
            marked as optional, and already answered (spec 102 US1 scenario 2).
          */
          setAnswers(initialAnswers(found));
          return;
        }
        if (checked.importable) {
          // Nothing to answer and nothing wrong: this press does what it has
          // always done, in the press it has always taken.
          send(file, null);
          return;
        }
        // What is left is a problem with the file itself, which renders below
        // as a refusal always has: no screen here could answer a misspelling.
      },
      onError: () => {
        // The check itself failed, which must not stand between somebody and
        // an upload they are entitled to attempt (FR-017).
        send(file, null);
      },
    });
  };

  const left = answers ? outstanding(rows, answers) : [];
  const blocked = resolving && left.length > 0;

  /*
    Everything wrong with the file, from whichever door said so. The preflight
    reports violations with a 200 and the import refuses with them; both render
    in the same region, because to the person reading it they are one thing.
  */
  const rejection = isWorkflowRejection(importMutation.error)
    ? importMutation.error.violations
    : isWorkflowRejection(importUpdateMutation.error)
      ? importUpdateMutation.error.violations
      : updateMode
        ? updateViolations
        : (report?.violations ?? []);
  const blockedRunning: RunningTask[] = isWorkflowUpdateBlocked(
    importUpdateMutation.error,
  )
    ? importUpdateMutation.error.running
    : running;
  const otherError =
    (importMutation.error && !isWorkflowRejection(importMutation.error)) ||
    (importUpdateMutation.error &&
      !isWorkflowRejection(importUpdateMutation.error) &&
      !isWorkflowUpdateBlocked(importUpdateMutation.error))
      ? (importMutation.error ?? importUpdateMutation.error)
      : null;

  return (
    <BundleImport
      workspaceId={workspaceId}
      file={bundle}
      onBusyChange={setBundleBusy}
      onDescribe={setDescribed}
      onDone={() => {
        onOpenChange(false);
        reset();
      }}
    >
      {(bundleParts) => (
        <FlowDialog
          open={open}
          onOpenChange={handleOpenChange}
          busy={isPending}
          title={
            bundle
              ? 'Upload a bundle'
              : updateMode
                ? 'Update this workflow'
                : 'Upload YAML or bundle'
          }
          description={
            bundle
              ? 'A bundle holds a workflow, the workflows it links to, and the agents and skills sent with it. Nothing is added until you import.'
              : updateMode
                ? `This file replaces ${updateTarget.name}'s definition in place. Its history and tasks stay; tasks on a removed step move to Backlog.`
                : resolving
                  ? 'This file names things that are not in this workspace. Choose what to use instead, or close to leave it.'
                  : 'Select a workflow definition file (.yaml or .yml, up to 1 MB), or a bundle exported from another workspace (.zip). Nothing is saved until it is checked.'
          }
          alert={bundle ? bundleParts.alert : otherError?.message}
          primary={
            bundle ? (
              bundleParts.action
            ) : (
              <Button
                onClick={submit}
                disabled={
                  !file ||
                  isPending ||
                  preCheckFailure !== null ||
                  blocked ||
                  updateBlocked
                }
              >
                {isPending
                  ? preflightMutation.isPending ||
                    preflightImportUpdateMutation.isPending
                    ? 'Checking…'
                    : 'Uploading…'
                  : updateMode
                    ? 'Update this workflow'
                    : resolving
                      ? 'Import'
                      : 'Upload'}
              </Button>
            )
          }
        >
          <div className="flex flex-col gap-2">
            <FileDrop
              // A file the upload does not take leaves the zone ready for
              // another, with the reason below it.
              file={preCheckFailure === null ? file : null}
              describe={
                file === null
                  ? ''
                  : bundle
                    ? described
                      ? `Bundle · ${described.root} · ${counted(described.items, 'item', 'items')}`
                      : `Bundle · ${file.name}`
                    : `Workflow file · ${file.name}`
              }
              invalid={preCheckFailure !== null}
              describedBy={
                preCheckFailure !== null ? preCheckErrorId : undefined
              }
              disabled={isPending}
              onFile={(selected) => {
                // A new file is a new question, so every answer to the old one
                // goes: they belonged to the file that prompted them (FR-013).
                reset();
                setFile(selected);
                const token = ++fileSelectionToken.current;
                const failure =
                  selected === null ? null : checkWorkflowFile(selected);
                setPreCheckFailure(failure);
                if (
                  selected !== null &&
                  failure === null &&
                  !isBundle(selected)
                ) {
                  void keyClash(selected, workflows).then((clash) => {
                    if (fileSelectionToken.current !== token) {
                      // A later file selection has already superseded this one.
                      return;
                    }
                    setUpdateTarget(clash);
                    if (clash) {
                      preflightImportUpdateMutation.mutate({
                        workflowId: clash.id,
                        file: selected,
                      });
                    }
                  });
                  return;
                }
                setUpdateTarget(null);
              }}
            />
            {preCheckFailure !== null && (
              <p
                id={preCheckErrorId}
                role="alert"
                className="text-destructive text-sm"
              >
                {preCheckFailure.message}
              </p>
            )}
          </div>

          {bundle ? (
            bundleParts.body
          ) : (
            <>
              {isPending && (
                <p role="status" className="text-muted-foreground text-sm">
                  {preflightMutation.isPending ||
                  preflightImportUpdateMutation.isPending
                    ? `Checking ${file?.name ?? 'the file'}…`
                    : `Uploading ${file?.name ?? 'the file'}…`}
                </p>
              )}

              {updateMode && updatePreflightFailed && (
                <p role="alert" className="text-destructive text-sm">
                  Could not check this workflow for running tasks, so the update
                  is blocked until the check succeeds. Try again.
                </p>
              )}

              {updateMode &&
                !updatePreflightFailed &&
                blockedRunning.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <p role="alert" className="text-destructive text-sm">
                      Stop these tasks, or wait for them to finish, before
                      updating this workflow.
                    </p>
                    <ul
                      aria-label="Running tasks"
                      className="flex flex-col gap-1.5"
                    >
                      {blockedRunning.map((task) => (
                        <RunningTaskItem key={task.task_id} task={task} />
                      ))}
                    </ul>
                  </div>
                )}

              {updateMode &&
                !updatePreflightFailed &&
                blockedRunning.length === 0 &&
                wouldMove.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <p className="text-muted-foreground text-sm">
                      These tasks will move to Backlog:
                    </p>
                    <ul
                      aria-label="Tasks moving to Backlog"
                      className="flex flex-col gap-1.5"
                    >
                      {wouldMove.map((task) => (
                        <MovedTaskItem key={task.task_id} task={task} />
                      ))}
                    </ul>
                  </div>
                )}

              {resolving && answers && (
                <ResolveReferences
                  rows={rows}
                  answers={answers}
                  onAnswersChange={setAnswers}
                  profiles={profiles.entries ?? []}
                  profilesUnavailable={profiles.loadFailed}
                  canCreateProfiles={isOrgAdmin}
                  people={people.people}
                  directoryUnavailable={people.failed}
                />
              )}

              {rejection.length > 0 && (
                <ul
                  role="alert"
                  aria-label="Import rejected"
                  className="flex flex-col gap-1.5"
                >
                  {rejection.map((violation, index) => (
                    <ViolationItem key={index} violation={violation} />
                  ))}
                </ul>
              )}
            </>
          )}
        </FlowDialog>
      )}
    </BundleImport>
  );
}

/**
 * What the import let go of, and what it was told to use instead (US4).
 *
 * The import succeeded, so neither is an error — but the workflow that exists
 * now is not quite the file it came from, and saying nothing would mean the
 * author discovers a gate routes to nobody only when it stalls (spec 067
 * FR-034). Both lists arrive on the same result and are reported together,
 * because a person who answered a question wants to see the answer took.
 */
function announce(
  warnings: readonly { message: string }[],
  substitutions: readonly {
    from_value: string;
    to_value: string;
    step_ids?: string[];
  }[],
) {
  const changes = substitutions.map(
    (applied) =>
      `${applied.from_value} → ${applied.to_value} on ${(applied.step_ids ?? []).join(', ')}`,
  );
  const dropped = warnings.map((warning) => warning.message);
  if (changes.length === 0 && dropped.length === 0) return;

  const headline =
    changes.length > 0
      ? `Imported with ${String(changes.length)} ${changes.length === 1 ? 'substitution' : 'substitutions'}.`
      : dropped.length === 1
        ? (dropped[0] ?? '')
        : `Imported with ${String(dropped.length)} things left out.`;
  const detail = [...changes, ...(changes.length > 0 ? dropped : [])];

  toast.warning(headline, {
    duration: 10000,
    description:
      detail.length > 0 && headline !== detail[0]
        ? detail.join(' ')
        : dropped.length > 1
          ? dropped.join(' ')
          : undefined,
  });
}

/**
 * What an in-place update moved, reported the same way a create-import
 * reports a substitution (FR-005): an empty list says "nothing moved" rather
 * than saying nothing.
 */
function announceUpdate(movedTasks: MovedTask[]) {
  if (movedTasks.length === 0) {
    toast.success('Workflow updated. Nothing moved.');
    return;
  }
  const names = movedTasks.map((task) => task.task_title);
  toast.success(
    `Workflow updated. ${String(movedTasks.length)} ${movedTasks.length === 1 ? 'task' : 'tasks'} moved to Backlog.`,
    { description: names.join(', ') },
  );
}
